import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import { API, API_MAND, fetchJson } from "../Utiles/api";
import { Save, RefreshCw, Search } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL;

const years = (() => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let y = 2017; y <= now; y++) arr.push(String(y));
  return arr;
})();

const quarters = ["Q1", "Q2", "Q3", "Q4"];

const resultadoOptions = [
  { value: "PENDIENTE", label: "PENDIENTE" },
  { value: "COBRADO", label: "COBRADO" },
  { value: "PENDIENTE_DEVUELTO", label: "PENDIENTE_DEVUELTO" },
];

const remesableOptions = [
  { value: "1", label: "Sí" },
  { value: "0", label: "No" },
];

const canalPagoOptions = [
  { value: "SEPA", label: "SEPA" },
  { value: "VENTANILLA", label: "VENTANILLA" },
];

const seqTpOptions = [
  { value: "RCUR", label: "RCUR" },
  { value: "FRST", label: "FRST" },
];

const getCurrentQuarter = () => `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;

const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const emptyForm = {
  colegiado_codigo: "",
  anio: String(new Date().getFullYear()),
  quarter: getCurrentQuarter(),
  importe: "",
  concepto: "",
  reqd_colltn_dt: todayIso(),
  resultado_banco: "PENDIENTE",
  remesable: "1",
  canal_pago: "SEPA",
  lote_id: "",
  end_to_end_id: "",
  mndt_id: "",
  seq_tp: "RCUR",
};

function generarEndToEndId() {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now()}${Math.floor(Math.random() * 1000000)}`;

  return uuid.slice(0, 35);
}

function normalizarImporte(valor) {
  if (valor == null) return "";
  return String(valor).replace(",", ".").trim();
}

function esImporteValido(valor) {
  return /^\d+(\.\d{1,2})?$/.test(normalizarImporte(valor));
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    Authorization: token ? `Bearer ${token}` : undefined,
    "Content-Type": "application/json",
  };
}

function sqlEscape(value) {
  return String(value ?? "").replace(/'/g, "''").trim();
}

function validarAnio(value) {
  return /^\d{4}$/.test(String(value || ""));
}

function validarQuarter(value) {
  return ["Q1", "Q2", "Q3", "Q4"].includes(String(value || ""));
}

function validarFechaIso(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function construirSQLInsertRecibo(form, periodo) {
  const codigo = Number(form.colegiado_codigo);
  const importe = Number(normalizarImporte(form.importe));
  const loteId = Number(form.lote_id);

  const resultadoBanco = sqlEscape(form.resultado_banco);
  const canalPago = sqlEscape(form.canal_pago);
  const seqTp = sqlEscape(form.seq_tp);
  const remesable = Number(form.remesable);
  const endToEndId = sqlEscape(form.end_to_end_id);
  const concepto = sqlEscape(form.concepto);
  const reqdColltnDt = sqlEscape(form.reqd_colltn_dt);

  const usarMandatoSepa =
    canalPago === "SEPA"
      ? `(
        SELECT ms.mndt_id
        FROM mandatos_sepa ms
        WHERE ms.colegiado_codigo = ${codigo}
          AND ms.activo = 1
        ORDER BY ms.id DESC
        LIMIT 1
      )`
      : "''";

  return `
INSERT INTO recibos (
  colegiado_codigo,
  periodo,
  importe,
  concepto,
  reqd_colltn_dt,
  resultado_banco,
  remesable,
  canal_pago,
  lote_id,
  end_to_end_id,
  mndt_id,
  seq_tp
)
SELECT
  ${codigo},
  '${sqlEscape(periodo)}',
  ${importe},
  '${concepto}',
  '${reqdColltnDt}',
  '${resultadoBanco}',
  ${remesable},
  '${canalPago}',
  ${loteId},
  '${endToEndId}',
  ${usarMandatoSepa},
  '${seqTp}'
FROM DUAL
WHERE NOT EXISTS (
  SELECT 1
  FROM recibos r
  WHERE r.colegiado_codigo = ${codigo}
    AND r.periodo = '${sqlEscape(periodo)}'
    AND r.lote_id = ${loteId}
);
`.trim();
}

const AddReciboPanel = ({ toast, setToast }) => {
  const [form, setForm] = useState({
    ...emptyForm,
    end_to_end_id: generarEndToEndId(),
  });

  const [saving, setSaving] = useState(false);
  const [loadingMandato, setLoadingMandato] = useState(false);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [mandatos, setMandatos] = useState([]);
  const [lotesDisponibles, setLotesDisponibles] = useState([]);
  const [errores, setErrores] = useState({});

  const periodo = useMemo(
    () => `${form.anio}${form.quarter}`,
    [form.anio, form.quarter]
  );

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrores((prev) => ({ ...prev, [key]: "" }));
  };

  const cargarLotesDisponibles = async (periodoActual) => {
    try {
      setLoadingLotes(true);

      const remesasData = await fetchJson(API.listar);
      const remesas = Array.isArray(remesasData) ? remesasData : [];

      const remesasDelPeriodo = remesas.filter(
        (r) => String(r.periodo || "").trim() === String(periodoActual).trim()
      );

      if (!remesasDelPeriodo.length) {
        setLotesDisponibles([]);
        setForm((prev) => ({ ...prev, lote_id: "" }));
        return;
      }

      const detalles = await Promise.allSettled(
        remesasDelPeriodo.map((r) => fetchJson(API.detalle(r.id)))
      );

      const lotes = [];

      detalles.forEach((res, idx) => {
        if (res.status !== "fulfilled" || !res.value) return;

        const detalle = res.value;
        const remesa = remesasDelPeriodo[idx];
        const lotesDetalle = Array.isArray(detalle?.lotes) ? detalle.lotes : [];

        for (const l of lotesDetalle) {
          lotes.push({
            id: l.id,
            remesaId: remesa.id,
            tipo: remesa.tipo || "",
            periodo: remesa.periodo || "",
            seq_tp: l.seq_tp ?? l.seqTp ?? "",
            reqd_colltn_dt: l.reqd_colltn_dt ?? l.reqdColltnDt ?? "",
            label: `Lote ${l.id} · ${remesa.tipo || ""} · ${l.seq_tp ?? l.seqTp ?? ""} · ${l.reqd_colltn_dt ?? l.reqdColltnDt ?? ""}`,
          });
        }
      });

      const unicos = Array.from(
        new Map(lotes.map((l) => [String(l.id), l])).values()
      ).sort((a, b) => Number(a.id) - Number(b.id));

      setLotesDisponibles(unicos);

      setForm((prev) => {
        const existeActual = unicos.some((l) => String(l.id) === String(prev.lote_id));
        return {
          ...prev,
          lote_id: existeActual ? prev.lote_id : (unicos[0]?.id ? String(unicos[0].id) : ""),
        };
      });
    } catch (e) {
      setLotesDisponibles([]);
      setForm((prev) => ({ ...prev, lote_id: "" }));
      setToast?.(e?.message || "Error cargando lotes disponibles");
    } finally {
      setLoadingLotes(false);
    }
  };

  useEffect(() => {
    if (!periodo) return;
    cargarLotesDisponibles(periodo);
  }, [periodo]);

  useEffect(() => {
    if (form.canal_pago === "VENTANILLA" && form.mndt_id) {
      setForm((prev) => ({ ...prev, mndt_id: "" }));
    }
  }, [form.canal_pago]);

  const cargarMandato = async () => {
    const codigo = String(form.colegiado_codigo || "").trim();
    if (!codigo) {
      setToast?.("Introduce un código de colegiado");
      return;
    }

    try {
      setLoadingMandato(true);
      const data = await fetchJson(API_MAND.listByColegiado(codigo));
      const lista = Array.isArray(data) ? data : [];
      setMandatos(lista);

      if (!lista.length) {
        setField("mndt_id", "");
        setToast?.("No hay mandatos para este colegiado");
        return;
      }

      const activos = lista.filter(
        (m) => m.activo === true || String(m.activo) === "true"
      );

      if (!activos.length) {
        setField("mndt_id", "");
        setToast?.("El colegiado no tiene mandatos activos");
        return;
      }

      const elegido = [...activos].sort(
        (a, b) => Number(b.id || 0) - Number(a.id || 0)
      )[0];

      const mndt = elegido?.mndtId ?? elegido?.mndt_id ?? "";
      setField("mndt_id", mndt);

      setToast?.(
        mndt
          ? `Mandato activo cargado: ${mndt}`
          : "Se encontró mandato activo pero sin mndt_id"
      );
    } catch (e) {
      setToast?.(e?.message || "Error cargando mandato");
    } finally {
      setLoadingMandato(false);
    }
  };

  const validar = () => {
    const err = {};
    const codigo = String(form.colegiado_codigo || "").trim();
    const lote = String(form.lote_id || "").trim();
    const importe = normalizarImporte(form.importe);
    const concepto = String(form.concepto || "").trim();
    const fechaPago = String(form.reqd_colltn_dt || "").trim();

    if (!codigo) err.colegiado_codigo = "Obligatorio";
    else if (!/^\d+$/.test(codigo)) err.colegiado_codigo = "Debe ser numérico";

    if (!form.anio) err.anio = "Obligatorio";
    else if (!validarAnio(form.anio)) err.anio = "Año inválido";

    if (!form.quarter) err.quarter = "Obligatorio";
    else if (!validarQuarter(form.quarter)) err.quarter = "Trimestre inválido";

    if (!importe) err.importe = "Obligatorio";
    else if (!esImporteValido(importe)) err.importe = "Usa punto para decimales. Ej: 58.56";
    else if (Number(importe) <= 0) err.importe = "Debe ser mayor que 0";

    if (!concepto) err.concepto = "Obligatorio";
    else if (concepto.length > 255) err.concepto = "Máximo 255 caracteres";

    if (!fechaPago) err.reqd_colltn_dt = "Obligatorio";
    else if (!validarFechaIso(fechaPago)) err.reqd_colltn_dt = "Fecha inválida";

    if (!["PENDIENTE", "COBRADO", "PENDIENTE_DEVUELTO"].includes(form.resultado_banco)) {
      err.resultado_banco = "Valor inválido";
    }

    if (!["1", "0"].includes(String(form.remesable))) {
      err.remesable = "Valor inválido";
    }

    if (!["SEPA", "VENTANILLA"].includes(form.canal_pago)) {
      err.canal_pago = "Valor inválido";
    }

    if (!lote) err.lote_id = "Selecciona un lote";
    else if (!/^\d+$/.test(lote)) err.lote_id = "Lote inválido";

    if (!form.end_to_end_id) err.end_to_end_id = "No generado";
    else if (String(form.end_to_end_id).length > 35) err.end_to_end_id = "Máximo 35 caracteres";

    if (!["RCUR", "FRST"].includes(form.seq_tp)) {
      err.seq_tp = "Valor inválido";
    }

    if (form.canal_pago === "SEPA" && !String(form.mndt_id || "").trim()) {
      err.mndt_id = "Carga el mandato activo del colegiado";
    }

    setErrores(err);
    return Object.keys(err).length === 0;
  };

  const resetForm = () => {
    setForm({
      ...emptyForm,
      anio: String(new Date().getFullYear()),
      quarter: getCurrentQuarter(),
      reqd_colltn_dt: todayIso(),
      end_to_end_id: generarEndToEndId(),
      lote_id: "",
    });
    setErrores({});
    setMandatos([]);
  };

  const guardar = async () => {
    if (!validar()) return;

    const sql = construirSQLInsertRecibo(form, periodo);

    try {
      setSaving(true);

      const limpio = sql
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/^--.*$/gm, "")
        .replace(/;\s*$/, "")
        .trim();

      const { data } = await axios.post(
        `${API_BASE}/api/admin/sql/exec`,
        { sql: limpio, modo: "DML", limite: 1 },
        { headers: authHeaders() }
      );

      const filasAfectadas =
        data?.affectedRows ??
        data?.rowsAffected ??
        data?.updateCount ??
        data?.count ??
        null;

      if (filasAfectadas === 0) {
        setToast?.(`No se insertó el recibo. Puede que ya exista para ${periodo} en ese lote.`);
        return;
      }

      setToast?.(`Recibo insertado correctamente para el colegiado ${form.colegiado_codigo}`);
      resetForm();
    } catch (e) {
      setToast?.(e?.response?.data?.message || e?.message || "Error insertando recibo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Añadir recibo manual</h2>
        <div className="flex gap-2">
          <Button variant="outline" iconLeft={<RefreshCw size={16} />} onClick={resetForm}>
            Limpiar
          </Button>
          <Button variant="primary" iconLeft={<Save size={16} />} onClick={guardar} disabled={saving}>
            {saving ? "Guardando..." : "Guardar recibo"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div>
          <FieldLabel label="Código colegiado" />
          <div className="flex gap-2">
            <TextInput
              value={form.colegiado_codigo}
              onChange={(e) => setField("colegiado_codigo", e.target.value)}
              placeholder="Ej: 3391"
              className="w-full"
            />
            <Button variant="outline" iconLeft={<Search size={16} />} onClick={cargarMandato} disabled={loadingMandato}>
              {loadingMandato ? "Cargando..." : "Cargar mandato"}
            </Button>
          </div>
          {errores.colegiado_codigo && <div className="text-red-600 text-xs mt-1">{errores.colegiado_codigo}</div>}
        </div>

        <div>
          <FieldLabel label="Mandato" />
          <TextInput
            value={form.mndt_id}
            onChange={(e) => setField("mndt_id", e.target.value)}
            placeholder="Se carga desde el colegiado activo"
            className="w-full"
            disabled={form.canal_pago !== "SEPA"}
          />
          <div className="text-xs text-gray-500 mt-1">
            Para SEPA debe existir mandato activo.
          </div>
          {errores.mndt_id && <div className="text-red-600 text-xs mt-1">{errores.mndt_id}</div>}
        </div>

        <div>
          <FieldLabel label="Año" />
          <Select value={form.anio} onChange={(e) => setField("anio", e.target.value)} className="w-full">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
          {errores.anio && <div className="text-red-600 text-xs mt-1">{errores.anio}</div>}
        </div>

        <div>
          <FieldLabel label="Trimestre" />
          <Select value={form.quarter} onChange={(e) => setField("quarter", e.target.value)} className="w-full">
            {quarters.map((q) => <option key={q} value={q}>{q}</option>)}
          </Select>
          {errores.quarter && <div className="text-red-600 text-xs mt-1">{errores.quarter}</div>}
        </div>

        <div>
          <FieldLabel label="Periodo calculado" />
          <TextInput value={periodo} disabled className="w-full bg-gray-50" />
        </div>

        <div>
          <FieldLabel label="Lote" />
          <Select
            value={form.lote_id}
            onChange={(e) => setField("lote_id", e.target.value)}
            className="w-full"
            disabled={loadingLotes || lotesDisponibles.length === 0}
          >
            {!loadingLotes && lotesDisponibles.length === 0 && (
              <option value="">Sin lotes disponibles para este periodo</option>
            )}
            {loadingLotes && <option value="">Cargando lotes...</option>}
            {!loadingLotes &&
              lotesDisponibles.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
          </Select>
          {lotesDisponibles.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Se muestran los lotes disponibles del periodo {periodo}.
            </div>
          )}
          {errores.lote_id && <div className="text-red-600 text-xs mt-1">{errores.lote_id}</div>}
        </div>

        <div>
          <FieldLabel label="Importe" />
          <TextInput
            value={form.importe}
            onChange={(e) => setField("importe", e.target.value)}
            placeholder="Ej: 58.56"
            className="w-full"
          />
          {errores.importe && <div className="text-red-600 text-xs mt-1">{errores.importe}</div>}
        </div>

        <div>
          <FieldLabel label="Concepto" />
          <TextInput
            value={form.concepto}
            onChange={(e) => setField("concepto", e.target.value)}
            placeholder="Ej: Cuota colegial"
            className="w-full"
          />
          {errores.concepto && <div className="text-red-600 text-xs mt-1">{errores.concepto}</div>}
        </div>

        <div>
          <FieldLabel label="Fecha de pago" />
          <input
            type="date"
            value={form.reqd_colltn_dt}
            onChange={(e) => setField("reqd_colltn_dt", e.target.value)}
            className="w-full rounded-2xl border border-gray-200 px-3 py-2"
          />
          {errores.reqd_colltn_dt && <div className="text-red-600 text-xs mt-1">{errores.reqd_colltn_dt}</div>}
        </div>

        <div>
          <FieldLabel label="Resultado banco" />
          <Select value={form.resultado_banco} onChange={(e) => setField("resultado_banco", e.target.value)} className="w-full">
            {resultadoOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {errores.resultado_banco && <div className="text-red-600 text-xs mt-1">{errores.resultado_banco}</div>}
        </div>

        <div>
          <FieldLabel label="Remesable" />
          <Select value={form.remesable} onChange={(e) => setField("remesable", e.target.value)} className="w-full">
            {remesableOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {errores.remesable && <div className="text-red-600 text-xs mt-1">{errores.remesable}</div>}
        </div>

        <div>
          <FieldLabel label="Canal pago" />
          <Select value={form.canal_pago} onChange={(e) => setField("canal_pago", e.target.value)} className="w-full">
            {canalPagoOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {errores.canal_pago && <div className="text-red-600 text-xs mt-1">{errores.canal_pago}</div>}
        </div>

        <div>
          <FieldLabel label="Recurrente / Primer pago" />
          <Select value={form.seq_tp} onChange={(e) => setField("seq_tp", e.target.value)} className="w-full">
            {seqTpOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {errores.seq_tp && <div className="text-red-600 text-xs mt-1">{errores.seq_tp}</div>}
        </div>

        <div>
          <FieldLabel label="E2E" />
          <div className="flex gap-2">
            <TextInput
              value={form.end_to_end_id}
              onChange={(e) => setField("end_to_end_id", e.target.value)}
              className="w-full font-mono text-xs"
            />
            <Button variant="outline" onClick={() => setField("end_to_end_id", generarEndToEndId())}>
              Regenerar
            </Button>
          </div>
          {errores.end_to_end_id && <div className="text-red-600 text-xs mt-1">{errores.end_to_end_id}</div>}
        </div>
      </div>

      {!!mandatos.length && (
        <div className="rounded-2xl border border-gray-200 p-3 bg-gray-50">
          <div className="font-medium mb-2">Mandatos encontrados</div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-4">ID</th>
                  <th className="pr-4">mndt_id</th>
                  <th className="pr-4">Fecha firma</th>
                  <th className="pr-4">Activo</th>
                </tr>
              </thead>
              <tbody>
                {mandatos.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="py-2 pr-4">{m.id}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{m.mndtId ?? m.mndt_id ?? ""}</td>
                    <td className="py-2 pr-4">{(m.fechaFirma ?? m.fecha_firma ?? "")?.slice?.(0, 10) || ""}</td>
                    <td className="py-2 pr-4">{m.activo === true || String(m.activo) === "true" ? "Sí" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReciboPanel;